import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import { navForUser, ROLE_LABELS } from '../lib/permissions';
import {
  LayoutDashboard, GitBranch, Users, TrendingUp, Map, Activity,
  DollarSign, Headphones, BarChart3, LogOut, ChevronRight,
  Calendar, Award, ArrowRightLeft, CheckSquare, ClipboardCheck, LineChart, UserCog,
  History, Store,
} from 'lucide-react';

const ICONS = {
  LayoutDashboard, GitBranch, Users, TrendingUp, Map, Activity,
  DollarSign, Headphones, BarChart3, Calendar, Award, ArrowRightLeft,
  CheckSquare, ClipboardCheck, LineChart, UserCog, History, Store,
};

function NavItem({ item, isActive, collapsed, onClick }) {
  const Icon = ICONS[item.icon] || LayoutDashboard;
  return (
    <button
      data-testid={`nav-${item.id}`}
      className={`nav-item ${isActive ? 'active' : ''}`}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
    >
      <Icon size={18} />
      {!collapsed && <span>{item.label}</span>}
    </button>
  );
}

function NavSection({ section, collapsed, isActive, onNavigate }) {
  return (
    <div className="nav-section">
      {!collapsed && <div className="nav-section-title">{section.title}</div>}
      {section.items.map(item => (
        <NavItem
          key={item.id}
          item={item}
          isActive={isActive(item.path)}
          collapsed={collapsed}
          onClick={() => onNavigate(item.path)}
        />
      ))}
    </div>
  );
}

function UserProfile({ user, collapsed }) {
  return (
    <div className="sidebar-user" title={user?.name} data-testid="sidebar-user">
      {!collapsed && (
        <div className="user-info">
          <div className="user-name" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="user-role" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const sections = navForUser(user);

  return (
    <nav data-testid="sidebar" className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-top">
        <div className="sidebar-logo" onClick={() => navigate('/')}>
          {collapsed
            ? <img src="/assets/logo-k.png" alt="K" style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
            : <img src="/assets/logo-kompa.png" alt="KOMPA" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
          }
        </div>
        <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
          <ChevronRight size={16} style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
        </button>
      </div>
      <div className="sidebar-nav">
        {sections.map(section => (
          <NavSection key={section.title} section={section} collapsed={collapsed} isActive={isActive} onNavigate={navigate} />
        ))}
      </div>
      <div className="sidebar-bottom">
        <UserProfile user={user} collapsed={collapsed} />
        <button data-testid="logout-button" className="nav-item logout-btn" onClick={handleLogout} title="Cerrar sesion">
          <LogOut size={18} />
          {!collapsed && <span>Salir</span>}
        </button>
      </div>
    </nav>
  );
}
